import { FC, ReactElement, useCallback } from 'react';

import { TThemeState } from '@/constants/theme';
import DarkThemeIcon from '@/icons/darkTheme';
import LightThemeIcon from '@/icons/lightTheme';
import { addRumAction, setGlobalContext } from '@/services/datadog';
import useHeaderState from '@/utils/hooks/useHeaderState';
import useThemeState from '@/utils/hooks/useThemeState';

interface IThemeToggleButtonProps {
	checked?: boolean;
	onChange?: () => void;
}

const ThemeToggleButton: FC<IThemeToggleButtonProps> = ({ checked = false, onChange }): ReactElement => {
	const { setTheme, theme: themeMode } = useThemeState();
	const { isChecked, setIsChecked } = useHeaderState();

	const onToggleDarkTheme = useCallback(() => {
		if (themeMode !== 'dark') {
			setTheme('dark');
			setIsChecked(true);

			// Execute some actions against datadog, just to track the use of the theme
			addRumAction('changeThemeColorTo', { theme: 'dark' });
			setGlobalContext('theme', 'dark' as TThemeState);

			onChange?.();
		}
	}, [setTheme, setIsChecked, themeMode, onChange]);

	const onToggleLightTheme = useCallback(() => {
		if (themeMode !== 'light') {
			setTheme('light');
			setIsChecked(false);

			// Execute some actions against datadog, just to track the use of the theme
			addRumAction('changeThemeColorTo', { theme: 'light' });
			setGlobalContext('theme', 'light' as TThemeState);

			onChange?.();
		}
	}, [setTheme, setIsChecked, themeMode, onChange]);

	return (
		<div
			id="theme-toggle-button"
			role="switch"
			data-testid="theme-toggle-button"
			aria-checked={isChecked}
			aria-labelledby="theme-toggle-button"
			className="relative flex cursor-pointer items-center rounded-lg bg-[#CBD0EB] p-1.5 dark:bg-darker-gray">
			<div className="flex gap-1.5">
				<button
					onClick={onToggleDarkTheme}
					aria-label="Activate dark theme"
					className={`flex h-7 w-7 items-center justify-center rounded-md p-1.5 ${
						themeMode === 'dark' ? 'bg-[#080F17]' : ''
					}`}>
					<DarkThemeIcon
						data-testid="dark-theme-icon"
						className={`h-full w-full ${themeMode === 'light' ? 'text-gray' : 'text-white'}`}
					/>
				</button>
				<button
					onClick={onToggleLightTheme}
					aria-label="Activate light theme"
					className={`flex h-7 w-7 items-center justify-center rounded-md p-1.5 ${
						themeMode === 'light' ? 'bg-white' : ''
					}`}>
					<LightThemeIcon data-testid="light-theme-icon" className="h-full w-full" />
				</button>
			</div>
		</div>
	);
};

export default ThemeToggleButton;
